import React, { Component } from "react";
import { Dropdown, Menu, Modal, Spin, Icon } from "antd";
import { Redirect } from "react-router-dom";
import NavBar from "../nav/NavBar/NavBar";
import Sidebar from "../../app/common/util/Sidebar";
import {
  errorToast,
  successToast,
  isSuperUser,
} from "../../app/common/util/genericUtils";
import {
  getWhatsappStatusApiCall,
  getWhatsappQrApiCall,
  logoutWhatsappApiCall,
} from "./api";
import WhatsappIcon from "./WhatsappIcon";

const QR_VALIDITY_SECONDS = 30;
const STATUS_POLL_MS = 5000;

class WhatsappPage extends Component {
  state = {
    loading: true,
    connected: false,
    phone: "",
    name: "",
    platform: "",
    qrVisible: false,
    qrLoading: false,
    qrData: "",
    qrSecondsLeft: QR_VALIDITY_SECONDS,
    loggingOut: false,
  };

  statusPollTimer = null;
  qrCountdownTimer = null;

  componentDidMount() {
    if (!isSuperUser()) {
      return;
    }
    this.fetchStatus();
  }

  componentWillUnmount() {
    this.clearQrWatchers();
  }

  clearQrWatchers = () => {
    if (this.statusPollTimer) {
      clearInterval(this.statusPollTimer);
      this.statusPollTimer = null;
    }
    if (this.qrCountdownTimer) {
      clearInterval(this.qrCountdownTimer);
      this.qrCountdownTimer = null;
    }
  };

  applyStatusData = (data) => {
    this.setState({
      connected: Boolean(data.connected),
      phone: data.phone || "",
      name: data.name || "",
      platform: data.platform || "",
    });
  };

  fetchStatus = async ({ silent = false } = {}) => {
    if (!silent) {
      this.setState({ loading: true });
    }
    try {
      const response = await getWhatsappStatusApiCall();
      const data = response.data || {};
      this.setState({
        connected: Boolean(data.connected),
        phone: data.phone || "",
        name: data.name || "",
        platform: data.platform || "",
        loading: false,
      });
      return data;
    } catch (err) {
      if (!silent) {
        this.setState({ loading: false });
        errorToast(
          "WhatsApp Status",
          (err.response && err.response.data && err.response.data.message) ||
            "Unable to fetch WhatsApp status."
        );
      }
      return null;
    }
  };

  startQrWatchers = () => {
    this.clearQrWatchers();
    this.setState({ qrSecondsLeft: QR_VALIDITY_SECONDS });

    this.qrCountdownTimer = setInterval(() => {
      this.setState(
        (prev) => ({
          qrSecondsLeft: Math.max(0, prev.qrSecondsLeft - 1),
        }),
        () => {
          if (this.state.qrSecondsLeft <= 0 && this.state.qrVisible) {
            this.handleQrExpired();
          }
        }
      );
    }, 1000);

    this.statusPollTimer = setInterval(() => {
      this.pollConnectionWhileQrOpen();
    }, STATUS_POLL_MS);
  };

  pollConnectionWhileQrOpen = async () => {
    if (!this.state.qrVisible || !this.state.qrData) {
      return;
    }
    try {
      const response = await getWhatsappStatusApiCall();
      const data = response.data || {};
      if (data.connected) {
        this.clearQrWatchers();
        this.applyStatusData(data);
        this.setState({
          qrVisible: false,
          qrData: "",
          qrLoading: false,
          qrSecondsLeft: QR_VALIDITY_SECONDS,
          loading: false,
        });
        successToast("WhatsApp", "Connected successfully.");
      }
    } catch (err) {
      // Keep QR open; transient poll failures should not interrupt scanning.
    }
  };

  handleQrExpired = () => {
    this.clearQrWatchers();
    this.setState({
      qrVisible: false,
      qrData: "",
      qrLoading: false,
      qrSecondsLeft: QR_VALIDITY_SECONDS,
    });
    errorToast(
      "QR Expired",
      "QR code is valid for 30 seconds only. Please try Connect again."
    );
    this.fetchStatus({ silent: true });
  };

  handleConnect = async () => {
    this.clearQrWatchers();
    this.setState({
      qrVisible: true,
      qrLoading: true,
      qrData: "",
      qrSecondsLeft: QR_VALIDITY_SECONDS,
    });
    try {
      const response = await getWhatsappQrApiCall();
      const data = response.data || {};
      if (data.connected) {
        this.setState({ qrVisible: false, qrLoading: false });
        successToast("WhatsApp", "Already connected.");
        this.fetchStatus();
        return;
      }
      if (!data.qr) {
        this.setState({ qrLoading: false });
        errorToast("WhatsApp QR", "No QR code received. Please try again.");
        return;
      }
      this.setState({ qrData: data.qr, qrLoading: false }, () => {
        this.startQrWatchers();
      });
    } catch (err) {
      this.setState({ qrLoading: false });
      errorToast(
        "WhatsApp QR",
        (err.response && err.response.data && err.response.data.message) ||
          "Unable to fetch QR code."
      );
    }
  };

  handleLogout = async () => {
    this.setState({ loggingOut: true });
    try {
      const response = await logoutWhatsappApiCall();
      const data = response.data || {};
      if (data.ok === false) {
        errorToast("WhatsApp Logout", "Logout failed. Please try again.");
      } else {
        successToast("WhatsApp", "Logged out successfully.");
      }
      await this.fetchStatus();
    } catch (err) {
      errorToast(
        "WhatsApp Logout",
        (err.response && err.response.data && err.response.data.message) ||
          "Unable to logout WhatsApp."
      );
    } finally {
      this.setState({ loggingOut: false });
    }
  };

  closeQrModal = () => {
    this.clearQrWatchers();
    this.setState({
      qrVisible: false,
      qrData: "",
      qrLoading: false,
      qrSecondsLeft: QR_VALIDITY_SECONDS,
    });
    this.fetchStatus({ silent: true });
  };

  renderSettingsMenu = () => {
    const { connected, loggingOut } = this.state;
    return (
      <Menu className="whatsapp-settings-menu">
        {!connected ? (
          <Menu.Item key="connect" onClick={this.handleConnect}>
            <Icon type="qrcode" />
            Connect WhatsApp
          </Menu.Item>
        ) : (
          <Menu.Item
            key="logout"
            onClick={this.handleLogout}
            disabled={loggingOut}
          >
            <Icon type="logout" />
            {loggingOut ? "Logging out..." : "Logout WhatsApp"}
          </Menu.Item>
        )}
      </Menu>
    );
  };

  renderStatusCard = () => {
    const { loading, connected, phone, name, platform } = this.state;

    if (loading) {
      return (
        <div className="whatsapp-status-card whatsapp-status-card--loading">
          <Spin size="large" />
          <p>Checking WhatsApp connection...</p>
        </div>
      );
    }

    if (!connected) {
      return (
        <div className="whatsapp-status-card whatsapp-status-card--disconnected">
          <div className="whatsapp-status-card__icon-wrap">
            <WhatsappIcon size={36} />
          </div>
          <div className="whatsapp-status-card__content">
            <h2 className="whatsapp-status-card__title">Disconnected</h2>
            <p className="whatsapp-status-card__message">
              Please connect WhatsApp first.
            </p>
            <p className="whatsapp-status-card__hint">
              Use Settings in the top right to scan a QR code and link your
              account.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="whatsapp-status-card whatsapp-status-card--connected">
        <div className="whatsapp-status-card__icon-wrap whatsapp-status-card__icon-wrap--live">
          <WhatsappIcon size={36} />
        </div>
        <div className="whatsapp-status-card__content">
          <div className="whatsapp-status-card__badge">Connected</div>
          <h2 className="whatsapp-status-card__title">WhatsApp is connected</h2>
          <p className="whatsapp-status-card__message">
            Your session is active and ready to receive messages.
          </p>
          <dl className="whatsapp-status-meta">
            {name ? (
              <div className="whatsapp-status-meta__row">
                <dt>Name</dt>
                <dd>{name}</dd>
              </div>
            ) : null}
            {phone ? (
              <div className="whatsapp-status-meta__row">
                <dt>Phone</dt>
                <dd>{phone}</dd>
              </div>
            ) : null}
            {platform ? (
              <div className="whatsapp-status-meta__row">
                <dt>Platform</dt>
                <dd>{platform}</dd>
              </div>
            ) : null}
          </dl>
        </div>
      </div>
    );
  };

  render() {
    if (!isSuperUser()) {
      return <Redirect to="/" />;
    }

    const { qrVisible, qrLoading, qrData, qrSecondsLeft } = this.state;
    const timerProgress = Math.max(
      0,
      (qrSecondsLeft / QR_VALIDITY_SECONDS) * 100
    );

    return (
      <div className="whatsapp-shell">
        <Sidebar />
        <NavBar />
        <main className="whatsapp-page">
          <header className="whatsapp-page__header">
            <div className="whatsapp-page__hero-text">
              <p className="whatsapp-page__eyebrow">Messaging</p>
              <h1 className="whatsapp-page__title">
                <WhatsappIcon className="whatsapp-page__title-icon" size={28} />
                WhatsApp
              </h1>
              <p className="whatsapp-page__subtitle">
                Manage your WhatsApp connection for order workflows.
              </p>
            </div>

            <Dropdown
              overlay={this.renderSettingsMenu()}
              trigger={["click"]}
              placement="bottomRight"
            >
              <button type="button" className="whatsapp-settings-btn">
                <Icon type="setting" />
                Settings
                <Icon type="down" className="whatsapp-settings-btn__caret" />
              </button>
            </Dropdown>
          </header>

          <section className="whatsapp-page__body">
            {this.renderStatusCard()}
          </section>
        </main>

        <Modal
          title="Connect WhatsApp"
          visible={qrVisible}
          onCancel={this.closeQrModal}
          footer={null}
          centered
          width={420}
          className="whatsapp-qr-modal"
          destroyOnClose
        >
          <div className="whatsapp-qr-modal__body">
            {qrLoading ? (
              <div className="whatsapp-qr-modal__loading">
                <Spin size="large" />
                <p>Generating QR code...</p>
              </div>
            ) : qrData ? (
              <React.Fragment>
                <div className="whatsapp-qr-timer">
                  <div className="whatsapp-qr-timer__header">
                    <Icon type="clock-circle" />
                    <span>
                      Valid for{" "}
                      <strong>
                        {qrSecondsLeft}s
                      </strong>{" "}
                      only
                    </span>
                  </div>
                  <div className="whatsapp-qr-timer__track">
                    <div
                      className={`whatsapp-qr-timer__fill${
                        qrSecondsLeft <= 10
                          ? " whatsapp-qr-timer__fill--urgent"
                          : ""
                      }`}
                      style={{ width: `${timerProgress}%` }}
                    />
                  </div>
                </div>
                <p className="whatsapp-qr-modal__hint">
                  Open WhatsApp on your phone and scan this code to link your
                  account.
                </p>
                <div className="whatsapp-qr-modal__frame">
                  <img src={qrData} alt="WhatsApp QR code" />
                </div>
                <p className="whatsapp-qr-modal__polling">
                  Waiting for scan… checking connection every 5 seconds
                </p>
              </React.Fragment>
            ) : (
              <p className="whatsapp-qr-modal__empty">
                Could not load QR code. Close and try again.
              </p>
            )}
          </div>
        </Modal>
      </div>
    );
  }
}

export default WhatsappPage;
